import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CancelTokenSource } from "axios";
import cn from "classnames";

import { Heading } from "rsuite";
import FunnelIcon from "@rsuite/icons/Funnel";

import constants from "utils/constants";

import useRefreshPage from "hooks/useRefreshPage";
import useInactiveEnvironment from "hooks/useInactiveEnvironment";
import useRequestCancellation from "hooks/useRequestCancellation";

import { loadD365RefreshParametersConfig } from "api/environments/tenants/tenant-info-api";
import { getErrorMessage } from "api/defaults";
import { loadSeqLogsLink } from "api/information-links-api";

import { type IAppSettings } from "store/app-settings/app-settings-types";
import {
    D365RefreshParametersStatus,
    type ITenant,
    type ITenantPingInfo,
    type IVersionInfo,
    TenantStatus,
    ViewMode,
} from "store/environments/tenants/tenants-types";
import { type ITenantVersionInfo } from "store/environments/tenants/tenant-status/tenant-status-types";
import { type RootState } from "store/store";

import CardView from "components/Environments/Tenants/TenantViews/CardView/CardView";
import ListView from "components/Environments/Tenants/TenantViews/ListView/ListView";

import ToggleButton from "components/Environments/Tenants/ToggleButton/ToggleButton";

import FiltersContainer from "components/Environments/Tenants/TenantFilters/TenantFiltersContainer";
import SearchBar from "components/SearchBar/SearchBarTenantsContainer";

import ErrorSectionContainer from "components/Common/ErrorSection/ErrorSectionContainer";

import "./Tenants.less";

interface Props {
    appSettings: IAppSettings;
    activeEnvMoniker: string;
    activeEnvironmentId: string;
    activeEnvironmentTenantIds: string[];
    modifiedTenants: ITenant[];
    errorMessage: string;
    tenantsPingInfo: ITenantPingInfo[];
    isLoaderVisible: boolean;
    isUserPartner: boolean;
    fetchEnvironments: (environmentId?: string) => void;
    fetchTenants: (id: string) => void;
    setActiveTenant: (tenant: ITenant) => void;
    pingTenantInD365: (
        envMoniker: string,
        tenantId: string,
        cancelTokenSource: CancelTokenSource
    ) => Promise<void>;
    setErrorResponseMessage: (msg: string) => void;
    setLoaderVisibility: (visible: boolean) => void;
    setTenantStatusOpened: (isOpened: boolean) => void;
    setTenantStatusInfo: (versionInfo: ITenantVersionInfo) => void;
    setTenantMoniker: (tenantMoniker: string) => void;
    setErrorMessageInactiveTenant: (errorMsg: string) => void;
    updateTenantVersionInfo: (
        tenantMoniker: string,
        versionInfo: IVersionInfo
    ) => void;
}

const Tenants: React.FC<Props> = ({
    appSettings,
    activeEnvMoniker,
    activeEnvironmentId,
    activeEnvironmentTenantIds,
    modifiedTenants,
    errorMessage,
    tenantsPingInfo,
    isLoaderVisible,
    isUserPartner,
    fetchEnvironments,
    fetchTenants,
    setActiveTenant,
    pingTenantInD365,
    setErrorResponseMessage,
    setLoaderVisibility,
    setTenantStatusOpened,
    setTenantStatusInfo,
    setTenantMoniker,
    setErrorMessageInactiveTenant,
    updateTenantVersionInfo,
}: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);

    const { environmentId } = useParams<{ environmentId: string }>() ?? "";
    const envMonikerFromUrl = useSelector(
        (state: RootState) =>
            state.environmentsReducer.environments.find(
                (env) => env.id === environmentId
            )?.moniker
    );
    const tenantPingTimeout =
        Number(appSettings.pingTimeout) || constants.pingTimeoutDefault;
    const [isD365ConfigLoaded, setIsD365ConfigLoaded] =
        useState<boolean>(false);
    const [isD365ParametersRefreshAllowed, setIsD365ParametersRefreshAllowed] =
        useState<boolean>(false);
    const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.List);
    const [seqLogsLink, setSeqLogsLink] = useState<string>("");
    const [isSeqLogsLinkLoaded, setSeqLogsLinkLoaded] =
        useState<boolean>(false);

    const cancelTokenSource = useRequestCancellation();

    const title = t("tenantDashboard.tenantsHeader");

    const routeToTenantConfiguration = (tenant: ITenant) => {
        setErrorMessageInactiveTenant("");
        if (!activeEnvironmentId || !activeEnvMoniker || !tenant) {
            return;
        }

        navigate(
            `/environments/${activeEnvironmentId}/tenants/${tenant.versionInfo.id}/general-configs`,
            {
                state: {
                    envMoniker: activeEnvMoniker,
                    tenant: tenant,
                },
            }
        );
    };

    const routeToTenantEnrollment = (enrollWorkflowId: string | null) => {
        if (!activeEnvironmentId || !enrollWorkflowId) {
            return;
        }

        navigate(
            `/environments/${activeEnvironmentId}/enroll-tenant/${enrollWorkflowId}`
        );
    };

    const routeToTenant = (tenant: ITenant) => {
        setActiveTenant(tenant);
        if (tenant.versionInfo.fetchStatus === TenantStatus.Enroll) {
            routeToTenantEnrollment(tenant.enrollWorkflowId);
        } else {
            routeToTenantConfiguration(tenant);
        }
    };

    const fetchD365ParametersConfig = (activeEnvMoniker: string) => {
        setErrorResponseMessage("");
        loadD365RefreshParametersConfig(activeEnvMoniker).then(
            ([payload, status]) => {
                if (payload !== null && status === constants.statusCode.OK) {
                    setIsD365ParametersRefreshAllowed(payload);
                } else {
                    setErrorResponseMessage(
                        getErrorMessage(String(payload), status)
                    );
                }
            }
        );
        setIsD365ConfigLoaded(true);
    };

    const fetchSeqLogsLink = () => {
        setErrorResponseMessage("");
        setLoaderVisibility(true);
        loadSeqLogsLink().then(([payload, statusCode]) => {
            if (payload && statusCode === constants.statusCode.OK) {
                setSeqLogsLink(payload);
            } else {
                setErrorResponseMessage(
                    getErrorMessage(String(payload), statusCode)
                );
            }
            setLoaderVisibility(false);
        });
        setSeqLogsLinkLoaded(true);
    };

    const getVersionStatusMessage = (tenant: ITenant) => {
        if (tenant.versionInfo.dooapVersion) {
            if (tenant.dooapVersionStatus === TenantStatus.Warning) {
                return `${tenant.versionInfo.dooapVersion} (${t(
                    "tenantDashboard.statusMessages.dooapVersionStatusWarning"
                )})`;
            } else if (tenant.dooapVersionStatus === TenantStatus.Error) {
                return `${tenant.versionInfo.dooapVersion} (${t(
                    "tenantDashboard.statusMessages.dooapVersionStatusError"
                )})`;
            } else {
                return tenant.versionInfo.dooapVersion;
            }
        } else {
            return t("unknown");
        }
    };

    const getErpVersion = (tenant: ITenant, value: string | null) => {
        return (
            <span
                className={cn(
                    {
                        "tenant-warning_status":
                            tenant.dooapVersionStatus === TenantStatus.Warning,
                    },
                    {
                        "tenant-error_status":
                            tenant.dooapVersionStatus === TenantStatus.Error &&
                            tenant.versionInfo.dooapVersion,
                    }
                )}
            >
                {value ?? getVersionStatusMessage(tenant)}
            </span>
        );
    };

    const getFetchStatusText = (fetchStatus: TenantStatus) => {
        switch (fetchStatus) {
            case TenantStatus.Error:
                return t("status.error");

            case TenantStatus.Enroll:
                return t("status.enroll");

            case TenantStatus.Deleting:
                return t("status.deleting");

            default:
                return t("status.normal");
        }
    };

    const getPingStatusText = (pingInfo: ITenantPingInfo | undefined) => {
        if (pingInfo?.errorMessage) {
            return t("status.error");
        } else {
            return t("status.normal");
        }
    };

    const getRefreshParametersText = (
        status: number,
        message: string | null
    ) => {
        switch (status) {
            case D365RefreshParametersStatus.Failed:
                return !message ? "-" : t("status.failed");

            default:
                return t("status.success");
        }
    };

    useEffect(() => {
        if (activeEnvMoniker && !isD365ConfigLoaded) {
            fetchD365ParametersConfig(activeEnvMoniker);
        }
    }, [activeEnvMoniker, isD365ConfigLoaded, setIsD365ConfigLoaded]);

    useEffect(() => {
        if (activeEnvMoniker && !isSeqLogsLinkLoaded) {
            fetchSeqLogsLink();
        }
    }, [activeEnvMoniker, isSeqLogsLinkLoaded]);

    useEffect(() => {
        if (!activeEnvMoniker) {
            fetchEnvironments(environmentId);
        }
    }, [activeEnvMoniker, environmentId, fetchEnvironments]);

    useEffect(() => {
        if (envMonikerFromUrl) {
            fetchTenants(envMonikerFromUrl);
        }
    }, [envMonikerFromUrl, fetchTenants]);

    //  Show Error on UI on accessing non-existing Env
    useInactiveEnvironment();

    useEffect(() => {
        const interval = setInterval(() => {
            activeEnvironmentTenantIds.forEach(async (tenantId) => {
                await pingTenantInD365(
                    activeEnvMoniker,
                    tenantId,
                    cancelTokenSource
                );
            });
        }, tenantPingTimeout);

        return () => clearInterval(interval);
    }, [
        activeEnvironmentTenantIds,
        activeEnvMoniker,
        pingTenantInD365,
        tenantPingTimeout,
    ]);

    useRefreshPage(
        useCallback(() => {
            if (envMonikerFromUrl) {
                containerRef.current?.scrollTo(0, 0);
                fetchEnvironments(environmentId);
                fetchTenants(envMonikerFromUrl);
            }
        }, [envMonikerFromUrl, environmentId])
    );

    if (errorMessage.length > 0) {
        return <ErrorSectionContainer />;
    }

    const tenantsView = () => {
        switch (viewMode) {
            case ViewMode.Card:
                return (
                    <CardView
                        modifiedTenants={modifiedTenants}
                        activeEnvMoniker={activeEnvMoniker}
                        activeEnvironmentId={activeEnvironmentId}
                        tenantsPingInfo={tenantsPingInfo}
                        isD365ParametersRefreshAllowed={
                            isD365ParametersRefreshAllowed
                        }
                        containerRef={containerRef}
                        seqLogsLink={seqLogsLink}
                        isUserPartner={isUserPartner}
                        isDeliverablesEnabled={
                            appSettings.isDeliverablesEnabled
                        }
                        routeToTenant={routeToTenant}
                        getErpVersion={getErpVersion}
                        getFetchStatusText={getFetchStatusText}
                        getPingStatusText={getPingStatusText}
                        getRefreshParametersText={getRefreshParametersText}
                        pingTenantInD365={pingTenantInD365}
                    />
                );
            default:
                return (
                    <ListView
                        modifiedTenants={modifiedTenants}
                        activeEnvMoniker={activeEnvMoniker}
                        activeEnvironmentId={activeEnvironmentId}
                        tenantsPingInfo={tenantsPingInfo}
                        isD365ParametersRefreshAllowed={
                            isD365ParametersRefreshAllowed
                        }
                        containerRef={containerRef}
                        isLoaderVisible={isLoaderVisible}
                        seqLogsLink={seqLogsLink}
                        isUserPartner={isUserPartner}
                        isDeliverablesEnabled={
                            appSettings.isDeliverablesEnabled
                        }
                        routeToTenant={routeToTenant}
                        getErpVersion={getErpVersion}
                        getVersionStatusMessage={getVersionStatusMessage}
                        getFetchStatusText={getFetchStatusText}
                        getPingStatusText={getPingStatusText}
                        getRefreshParametersText={getRefreshParametersText}
                        setTenantStatusOpened={setTenantStatusOpened}
                        setTenantStatusInfo={setTenantStatusInfo}
                        setTenantMoniker={setTenantMoniker}
                        updateTenantVersionInfo={updateTenantVersionInfo}
                    />
                );
        }
    };

    return (
        <div className="tenants__container">
            <div className="tenants__header">
                <Heading style={{ marginBottom: "0.5rem" }} level={1}>
                    {title}
                </Heading>

                <div className="tenants__search-section">
                    <SearchBar />
                    <FunnelIcon
                        style={{ fontSize: "2.5em", margin: "0 24px" }}
                    />
                    <FiltersContainer />
                    <ToggleButton
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                    />
                </div>
            </div>
            {tenantsView()}
        </div>
    );
};

export default Tenants;
